import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, RadioGroup,
  FormControlLabel, Radio, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Box, Typography, IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import GovernmentInvoice from '../../models/governmentInvoice';

const formatVND = (n) => `${(Number(n) || 0).toLocaleString('vi-VN')} đ`;
const lineTotal = (l) => Math.round((Number(l.quantity) || 0) * (Number(l.unit_price) || 0));

/** Create an adjustment (TCHDon 2/3/4) draft for a signed HDDT invoice. */
const HddtAdjustDialog = ({ open, onClose, governmentInvoice, onDone }) => {
  const [adjustType, setAdjustType] = useState('up');  // up(2) / down(3) / info(4)
  const [lines, setLines] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateLine = (i, field, value) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const addLine = () => setLines((p) => [...p, { description: '', quantity: 1, unit_price: 0 }]);
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + lineTotal(l), 0);

  const submit = async () => {
    setSubmitting(true);
    try {
      const gi = new GovernmentInvoice(governmentInvoice);
      const result = await gi.adjust({
        adjust_type: adjustType,
        line_items: lines.map((l) => ({
          description: l.description, quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0, total_price: lineTotal(l),
        })),
        reason,
      });
      if (result) {
        toast.success('Đã tạo hóa đơn điều chỉnh (nháp); ký để phát hành');
        if (onDone) onDone(result);
        onClose();
      } else {
        toast.error('Không thể tạo hóa đơn điều chỉnh');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Lỗi khi điều chỉnh');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Điều chỉnh hóa đơn điện tử {governmentInvoice?.hddt_sh_don || ''}</DialogTitle>
      <DialogContent dividers>
        <RadioGroup row value={adjustType} onChange={(e) => setAdjustType(e.target.value)} sx={{ mb: 2 }}>
          <FormControlLabel value="up" control={<Radio />} label="Điều chỉnh tăng" />
          <FormControlLabel value="down" control={<Radio />} label="Điều chỉnh giảm" />
          <FormControlLabel value="info" control={<Radio />} label="Điều chỉnh thông tin" />
        </RadioGroup>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Mô tả</TableCell>
              <TableCell align="right">SL</TableCell>
              <TableCell align="right">Đơn giá</TableCell>
              <TableCell align="right">Thành tiền</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, i) => (
              <TableRow key={i}>
                <TableCell><TextField variant="standard" value={l.description}
                  onChange={(e) => updateLine(i, 'description', e.target.value)} /></TableCell>
                <TableCell align="right"><TextField variant="standard" type="number" value={l.quantity}
                  onChange={(e) => updateLine(i, 'quantity', e.target.value)} sx={{ width: 70 }}
                  inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                <TableCell align="right"><TextField variant="standard" type="number" value={l.unit_price}
                  onChange={(e) => updateLine(i, 'unit_price', e.target.value)} sx={{ width: 110 }}
                  inputProps={{ style: { textAlign: 'right' } }} /></TableCell>
                <TableCell align="right">{formatVND(lineTotal(l))}</TableCell>
                <TableCell><IconButton size="small" onClick={() => removeLine(i)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} align="right"><strong>TỔNG</strong></TableCell>
              <TableCell align="right"><strong>{formatVND(total)}</strong></TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        <Button startIcon={<AddIcon />} onClick={addLine} sx={{ mt: 1 }}>Thêm dòng</Button>
        <TextField label="Lý do điều chỉnh" value={reason} onChange={(e) => setReason(e.target.value)}
          multiline rows={2} fullWidth sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Hủy</Button>
        <Button onClick={submit} variant="contained" disabled={submitting}>
          {submitting ? 'Đang tạo...' : 'Tạo HĐ điều chỉnh'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HddtAdjustDialog;
